import {
  ArrowRightIcon,
  ChatBubbleOvalLeftEllipsisIcon,
  PlusIcon,
  UserIcon,
} from "@heroicons/react/24/outline";
import { defineMessages, FormattedMessage, type IntlShape } from "react-intl";
import type { ActionCard } from "./ActionCarousel";

const TITLES = defineMessages({
  addIncome: {
    id: "app.poaOnb.waiting.addIncome.title",
    defaultMessage: "Lägg till inkomst",
  },
  advisory: {
    id: "app.poaOverview.advisory.headline",
    defaultMessage: "Få hjälp av en rådgivare",
  },
  askSam: {
    id: "app.poaOverview.askSam.headline",
    defaultMessage: "Fråga Sam om pension",
  },
  seeDetails: {
    id: "app.poaOverview.moreDetails.title",
    defaultMessage: "Se fler detaljer",
  },
});

export function buildActionCards({
  intl,
  onAddIncome,
  onGetAdvice,
  onAskSam,
  onSeeDetails,
  hasIncome,
  isExistingCustomer,
}: {
  intl: IntlShape;
  onAddIncome: () => void;
  /** Omitted once the customer has sent the request. */
  onGetAdvice?: () => void;
  onAskSam?: () => void;
  onSeeDetails: () => void;
  hasIncome: boolean;
  isExistingCustomer: boolean;
}): ActionCard[] {
  const cards: ActionCard[] = [];

  // The design ties this card to one thing only: no income yet, gone once it is added.
  if (!hasIncome) {
    cards.push({
      id: "addIncome",
      icon: <PlusIcon width={20} height={20} aria-hidden="true" />,
      title: intl.formatMessage(TITLES.addIncome),
      description: (
        <FormattedMessage
          id="app.poaOnb.waiting.addIncome.description"
          defaultMessage="Vi behöver din månadsinkomst för att kunna visa din förväntade pension."
        />
      ),
      onClick: onAddIncome,
    });
  }

  // PEN-169: offered to every customer, in the collection state as well as the finished
  // overview, and it sits right after the income card. The copy promises a conversation,
  // never a better pension, because nobody has looked at the data yet.
  if (onGetAdvice) {
    cards.push({
      id: "advisory",
      icon: <UserIcon width={20} height={20} aria-hidden="true" />,
      title: intl.formatMessage(TITLES.advisory),
      description: (
        <FormattedMessage
          id="app.poaOverview.advisory.description"
          defaultMessage="En rådgivare kan gå igenom din pension med dig när uppgifterna har hämtats."
        />
      ),
      onClick: onGetAdvice,
    });
  }

  // The container passes the handler only when the app build actually has the chat.
  if (onAskSam) {
    cards.push({
      id: "askSam",
      icon: (
        <ChatBubbleOvalLeftEllipsisIcon
          width={20}
          height={20}
          aria-hidden="true"
        />
      ),
      title: intl.formatMessage(TITLES.askSam),
      description: (
        <FormattedMessage
          id="app.poaOverview.askSam.description"
          defaultMessage="Vår AI-chatt kan hjälpa dig med de funderingar du har kring pension."
        />
      ),
      onClick: onAskSam,
    });
  }

  // The updated design drops this card for POA customers; it belongs to the existing
  // customer variant, which the route hands to the Sambla Pension screen today.
  if (isExistingCustomer) {
    cards.push({
      id: "seeDetails",
      icon: <ArrowRightIcon width={20} height={20} aria-hidden="true" />,
      title: intl.formatMessage(TITLES.seeDetails),
      description: (
        <FormattedMessage
          id="app.poaOverview.moreDetails.description"
          defaultMessage="Du har ett pensionskonto hos oss. Gå till Sambla Pension för att se mer."
        />
      ),
      onClick: onSeeDetails,
    });
  }

  return cards;
}
